import { ReactNode } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import { tokens } from "../../theme";

/**
 * Stepper vertical del Design Kit (§ Stepper del proceso). Cada paso es un círculo
 * con estado + un conector hacia el siguiente, y a la derecha su título/subtítulo.
 * Genérico: recibe los ítems ya resueltos (color por `tono`), sin lógica de dominio.
 */
export type TonoPaso = "completado" | "actual" | "pendiente" | "reprobado" | "hito";

export interface PasoStepper {
  titulo: string;
  /** Texto bajo el título; admite nodos para poder enlazar (p. ej. al tema). */
  subtitulo?: ReactNode;
  tono: TonoPaso;
  /** Ícono del círculo (p. ej. para eventos de tema). Si falta, se usa el del tono. */
  icono?: ReactNode;
  /** Etiqueta chica junto al título (p. ej. "Actual"). */
  badge?: string;
  /** Acción opcional bajo el subtítulo (p. ej. descargar el informe del paso). */
  accion?: ReactNode;
  /** Contenido opcional bajo el subtítulo (p. ej. la nota y las correcciones). */
  contenido?: ReactNode;
}

// Colores de círculo, conector y título por tono — todo desde `tokens`.
function estilo(tono: TonoPaso) {
  switch (tono) {
    case "completado":
      return { bg: tokens.estado.exito, fg: tokens.neutro.blanco, borde: "none", conector: tokens.estado.exito, titulo: tokens.neutro.texto, halo: false };
    case "actual":
      return { bg: tokens.azul[600], fg: tokens.neutro.blanco, borde: "none", conector: tokens.neutro.borde, titulo: tokens.marca.texto, halo: true };
    case "reprobado":
      return { bg: tokens.estado.error, fg: tokens.neutro.blanco, borde: "none", conector: tokens.estado.error, titulo: tokens.neutro.texto, halo: false };
    case "hito":
      return { bg: tokens.azul[600], fg: tokens.neutro.blanco, borde: "none", conector: tokens.neutro.borde, titulo: tokens.neutro.texto, halo: false };
    default: // pendiente
      return { bg: tokens.neutro.blanco, fg: tokens.neutro.textoTenue, borde: `2px solid ${tokens.boton.bordeSecundario}`, conector: tokens.neutro.borde, titulo: tokens.neutro.textoTenue, halo: false };
  }
}

function iconoDelTono(tono: TonoPaso): ReactNode | null {
  if (tono === "completado") return <CheckIcon sx={{ fontSize: 15 }} />;
  if (tono === "reprobado") return <CloseIcon sx={{ fontSize: 15 }} />;
  return null;
}

export default function Stepper({ items }: { items: PasoStepper[] }) {
  return (
    <Box>
      {items.map((it, i) => {
        const e = estilo(it.tono);
        const icono = it.icono ?? iconoDelTono(it.tono);
        const esUltimo = i === items.length - 1;
        return (
          <Box key={i} sx={{ display: "flex", gap: 1.75 }}>
            {/* Columna: círculo + conector */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  bgcolor: e.bg,
                  color: e.fg,
                  border: e.borde,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  ...(e.halo ? { boxShadow: `0 0 0 4px ${tokens.marca.tenue}` } : {}),
                }}
              >
                {icono ?? <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "currentColor" }} />}
              </Box>
              {!esUltimo && <Box sx={{ width: 2, flex: 1, minHeight: 26, bgcolor: e.conector, my: 0.25 }} />}
            </Box>
            {/* Texto */}
            <Box sx={{ pt: 0.4, pb: esUltimo ? 0 : 2.25 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: e.titulo }}>{it.titulo}</Typography>
                {it.badge && (
                  <Box
                    component="span"
                    sx={{ fontSize: 11, fontWeight: 600, bgcolor: tokens.marca.tenue, color: tokens.marca.texto, px: 0.9, py: 0.2, borderRadius: 0.75 }}
                  >
                    {it.badge}
                  </Box>
                )}
                {it.accion}
              </Stack>
              {it.subtitulo && <Typography sx={{ fontSize: 12, color: tokens.neutro.textoTenue, mt: 0.25 }}>{it.subtitulo}</Typography>}
              {it.contenido}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
